import React, { useState, useEffect } from 'react';
import { Search, CreditCard, User, FileText, Calendar, BadgeCheck, X, AlertCircle, Filter, CheckCircle, Eye, ChevronLeft, ChevronRight, Loader2, SearchX } from 'lucide-react';
import API_BASE_URL from '../Config';
import { useAuth } from '../contexts/AuthContext';

export default function HistorialCreditos() {
    const { token } = useAuth();

    // --- Unified Search States ---
    const [searchMethod, setSearchMethod] = useState('CREDITO_ID');
    const [searchValue, setSearchValue] = useState('');

    // --- Data States ---
    const [resultType, setResultType] = useState(null); // 'SOCIO_CREDITOS' or 'SOCIO_PAGOS' or 'CREDITO_DETALLE'
    const [dataList, setDataList] = useState([]); // Can be Credits or Payments or Installments
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);

    // --- Modal States ---
    const [showModal, setShowModal] = useState(false);
    const [selectedCredit, setSelectedCredit] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalCuotas, setModalCuotas] = useState([]);
    const [modalPage, setModalPage] = useState(0);
    const [modalSize, setModalSize] = useState(10);
    const [modalTotalPages, setModalTotalPages] = useState(0);
    const [modalFilter, setModalFilter] = useState(''); // 'PENDIENTE', 'PAGADO', ''

    // --- Auxiliary Data for Socio Header ---
    const [socioInfo, setSocioInfo] = useState(null);

    // --- Search Logic ---

    const handleSearch = async (e) => {
        if (e) e.preventDefault();
        if (!searchValue) return;

        setError(null);
        setDataList([]);
        setSocioInfo(null);
        setResultType(null);
        setCurrentPage(0);

        if (searchMethod === 'CREDITO_ID') {
            fetchDetalleCredito(searchValue);
        } else if (searchMethod === 'SOCIO_ID') {
            fetchResumenSocio(searchValue);
            fetchSocioInfo(searchValue);
        } else if (searchMethod === 'DOC') {
            searchByDoc(searchValue);
        }
    };

    const searchByDoc = async (doc) => {
        setLoading(true);
        try {
            const resp = await fetch(`${API_BASE_URL}/socios/buscar?documento=${doc}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!resp.ok) throw new Error('Error al buscar socio');
            const data = await resp.json();
            if (data && data.length > 0) {
                const socioId = data[0].id;
                fetchSocioInfo(socioId);
                fetchResumenSocio(socioId);
            } else {
                setError('Socio no encontrado con ese documento.');
                setLoading(false);
            }
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    // --- Data Fetchers ---

    const fetchResumenSocio = async (socioId) => {
        setLoading(true);
        setResultType('SOCIO_CREDITOS');
        try {
            const response = await fetch(`${API_BASE_URL}/creditos/socio/${socioId}/resumen`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Error cargando resumen de créditos');
            const data = await response.json();
            setDataList(data || []);
            setTotalPages(1); // Resume is not paginated usually
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchPagosSocio = async (socioId, page = 0) => {
        setLoading(true);
        setResultType('SOCIO_PAGOS');
        try {
            const response = await fetch(`${API_BASE_URL}/creditos/pagos/socio/${socioId}?page=${page}&size=10&sort=fechaPago,desc`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Error cargando historial de pagos');
            const data = await response.json();
            setDataList(data.content || []);
            setTotalPages(data.totalPages);
            setCurrentPage(page);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDetalleCredito = async (creditoId) => {
        // Direct search of a credit opens the modal directly or shows it as main result
        // Let's show it as main result for now but we'll use the modal logic for consistency
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/creditos/${creditoId}/detalle`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Crédito no encontrado');
            const cred = await response.json();
            setSocioInfo(cred.socio);
            handleVerDetalle(cred);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchSocioInfo = async (id) => {
        try {
            const res = await fetch(`${API_BASE_URL}/socio/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) setSocioInfo(await res.json());
        } catch (e) { console.error(e); }
    };

    // --- Modal Logic ---

    const handleVerDetalle = (credito) => {
        setSelectedCredit(credito);
        setShowModal(true);
        fetchModalCuotas(credito.id, 0, modalSize, modalFilter);
    };

    const fetchModalCuotas = async (creditoId, page, size, estado) => {
        setModalLoading(true);
        try {
            let url = `${API_BASE_URL}/creditos/${creditoId}/cuotas?page=${page}&size=${size}&sort=numeroCuota,asc`;
            if (estado) url += `&estado=${estado}`;

            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Error cargando cuotas');
            const data = await response.json();
            setModalCuotas(data.content || []);
            setModalTotalPages(data.totalPages);
            setModalPage(page);
        } catch (err) {
            console.error(err);
        } finally {
            setModalLoading(false);
        }
    };

    // --- Helpers ---
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-PY') : '-';
    const formatCurrency = (v) => new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(v || 0);

    const getPlaceholder = () => {
        switch (searchMethod) {
            case 'CREDITO_ID': return 'Ej: 850';
            case 'SOCIO_ID': return 'Ej: 10025';
            case 'DOC': return 'Ej: 4.500.123';
            default: return 'Buscar...';
        }
    };
    // --- Modal Status Styles ---
    const getStatusStyle = (cuota) => {
        const status = (cuota.estadoDescripcion || cuota.estadoCuota || '').toUpperCase();
        if (status.includes('CANCELADO') || status.includes('PAGADO')) return 'bg-green-100 text-green-700 border border-green-200';
        if (status.includes('PARCIAL')) return 'bg-orange-100 text-orange-700 border border-orange-200';
        if (status.includes('VENCIDA') || status.includes('MORA')) return 'bg-red-100 text-red-700 border border-red-200';
        return 'bg-slate-100 text-slate-900 border border-slate-200';
    };

    const getStatusText = (cuota) => {
        return cuota.estadoDescripcion || cuota.estadoCuota || 'PENDIENTE';
    };

    const isOverdue = (fecha) => {
        if (!fecha) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return new Date(fecha) < today;
    };

    // --- Sub-Modal for Payment Details ---
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedCuotaForPayment, setSelectedCuotaForPayment] = useState(null);
    const [payments, setPayments] = useState([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);

    const handleVerDetallePago = async (cuota) => {
        setSelectedCuotaForPayment(cuota);
        setShowPaymentModal(true);
        setPaymentsLoading(true);
        try {
            const socioIdToUse = socioInfo?.id || selectedCredit?.idSocio;
            if (!socioIdToUse) throw new Error('No se pudo identificar al socio');

            const response = await fetch(`${API_BASE_URL}/creditos/pagos/socio/${socioIdToUse}?page=0&size=50&sort=fechaPago,desc`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                const targetCreditoId = cuota.creditoId || selectedCredit?.id;
                const filtered = (data.content || []).filter(p => 
                    Number(p.creditoId) === Number(targetCreditoId) && 
                    (Number(p.numeroCuota) === Number(cuota.numeroCuota) || Number(p.nroCuota) === Number(cuota.numeroCuota))
                );
                setPayments(filtered);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setPaymentsLoading(false);
        }
    };

    return (
        <div className="space-y-5">
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-700" />
                Historial de Créditos
            </h1>

            {/* UNIFIED SEARCH BAR */}
            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
                <div className="flex flex-col md:flex-row gap-4 mb-2">
                    <div className="flex-shrink-0">
                        <label className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">Buscar Por:</label>
                        <select
                            value={searchMethod}
                            onChange={(e) => {
                                setSearchMethod(e.target.value);
                                setSearchValue('');
                                setError(null);
                            }}
                            className="w-full md:w-48 p-3 border border-gray-200 rounded-xl font-bold text-slate-700 focus:ring-2 focus:ring-green-500 outline-none bg-gray-50 hover:bg-white transition cursor-pointer"
                        >
                            <option value="CREDITO_ID">Nro. Crédito</option>
                            <option value="SOCIO_ID">Nro. Socio</option>
                            <option value="DOC">C.I. / RUC</option>
                        </select>
                    </div>

                    <div className="flex-grow relative">
                        <label className="block text-xs font-bold text-gray-400 mb-1 uppercase tracking-wider">
                            {searchMethod === 'CREDITO_ID' ? 'Ingrese Número de Operación' :
                                searchMethod === 'SOCIO_ID' ? 'Ingrese Código de Socio' : 'Ingrese Documento'}
                        </label>
                        <form onSubmit={handleSearch} className="flex gap-2">
                            <input
                                type={searchMethod === 'DOC' ? 'text' : 'number'}
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                placeholder={getPlaceholder()}
                                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none text-lg font-medium placeholder:font-normal placeholder:text-gray-300"
                                autoFocus
                            />
                            <button
                                type="submit"
                                className="bg-green-700 text-white px-6 py-3 rounded-xl font-bold hover:bg-green-800 transition shadow-lg shadow-green-200 flex items-center gap-2 active:scale-95"
                            >
                                <Search className="w-5 h-5" />
                                BUSCAR
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* RESULTS AREA */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden min-h-[400px]">

                {/* RESULTS HEADER */}
                {socioInfo && (
                    <div className="bg-slate-50 border-b border-gray-200 p-4 flex flex-col md:flex-row justify-between items-center gap-4 animate-in fade-in">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Socio</span>
                                <h2 className="text-lg font-bold text-slate-800">
                                    {socioInfo.personaFisica ? `${socioInfo.personaFisica.nombres} ${socioInfo.personaFisica.apellidos}` : socioInfo.personaJuridica?.razonSocial}
                                </h2>
                            </div>
                            <p className="text-xs text-slate-500 font-mono">ID: {socioInfo.id} | DOC: {socioInfo.numeroDocumento}</p>
                        </div>

                        <div className="flex bg-white rounded-lg p-1 border border-gray-200 shadow-sm">
                            <button
                                onClick={() => fetchResumenSocio(socioInfo.id)}
                                className={`px-4 py-2 rounded-md text-sm font-bold transition flex items-center gap-2
                                    ${resultType === 'SOCIO_CREDITOS' ? 'bg-slate-800 text-white shadow-md' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}
                                `}
                            >
                                <CreditCard className="w-4 h-4" />
                                Créditos
                            </button>
                            <button
                                onClick={() => fetchPagosSocio(socioInfo.id)}
                                className={`px-4 py-2 rounded-md text-sm font-bold transition flex items-center gap-2
                                    ${resultType === 'SOCIO_PAGOS' ? 'bg-green-600 text-white shadow-md' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}
                                `}
                            >
                                <CheckCircle className="w-4 h-4" />
                                Pagos Realizados
                            </button>
                        </div>
                    </div>
                )}

                <div className="p-6">
                    {loading ? (
                        <div className="py-12 flex flex-col items-center justify-center text-gray-400">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mb-3"></div>
                            <p>Cargando...</p>
                        </div>
                    ) : error ? (
                        <div className="py-12 bg-red-50 rounded-xl flex flex-col items-center justify-center text-red-600 border border-red-100 animate-in zoom-in duration-300">
                            <AlertCircle className="w-10 h-10 mb-3" />
                            <p className="font-bold">{error}</p>
                        </div>
                    ) : dataList.length === 0 ? (
                        <div className="py-20 text-center text-gray-400 flex flex-col items-center">
                            <Search className="w-16 h-16 mb-4 opacity-20" />
                            <p className="text-lg">Ingrese un criterio para visualizar el historial.</p>
                        </div>
                    ) : (
                        <div className="animate-in fade-in">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-500 uppercase text-xs tracking-wider border-b">
                                            {resultType === 'SOCIO_CREDITOS' ? (
                                                <>
                                                    <th className="px-6 py-3 text-left">Nro Operación</th>
                                                    <th className="px-6 py-3 text-left">F. Desembolso</th>
                                                    <th className="px-6 py-3 text-right">Monto</th>
                                                    <th className="px-6 py-3 text-center">Estado</th>
                                                    <th className="px-6 py-3 text-center">Acción</th>
                                                </>
                                            ) : (
                                                <>
                                                    <th className="px-6 py-3 text-left">Fecha Pago</th>
                                                    <th className="px-6 py-3 text-left">Concepto</th>
                                                    <th className="px-6 py-3 text-right">Monto Abonado</th>
                                                    <th className="px-6 py-3 text-right">Saldo Cuota</th>
                                                    <th className="px-6 py-3 text-center">Info</th>
                                                </>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {dataList.map((item, idx) => (
                                            <tr key={item.id || idx} className="hover:bg-slate-50 transition border-l-4 border-l-transparent hover:border-l-green-500">
                                                {resultType === 'SOCIO_CREDITOS' ? (
                                                    <>
                                                        <td className="px-6 py-4 font-bold text-slate-700">{item.nroOperacion || `#${item.id}`}</td>
                                                        <td className="px-6 py-4">{formatDate(item.fechaDesembolso)}</td>
                                                        <td className="px-6 py-4 text-right font-bold text-slate-800">{formatCurrency(item.monto)}</td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase
                                                                ${item.estadoNombre === 'Vigente' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}
                                                            `}>
                                                                {item.estadoNombre}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            <button
                                                                onClick={() => handleVerDetalle(item)}
                                                                className="p-2 text-green-700 hover:bg-green-50 rounded-lg transition"
                                                                title="Ver Cuotas"
                                                            >
                                                                <Eye className="w-5 h-5" />
                                                            </button>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-6 py-4 font-bold text-gray-700">{formatDate(item.fechaPago)}</td>
                                                        <td className="px-6 py-4">
                                                            <div className="font-bold text-slate-700">{item.concepto}</div>
                                                            <div className="text-[10px] text-gray-400">Cuota #{item.numeroCuota}</div>
                                                        </td>
                                                        <td className="px-6 py-4 text-right font-bold text-green-700">{formatCurrency(item.montoPagado)}</td>
                                                        <td className="px-6 py-4 text-right font-bold text-gray-500">{formatCurrency(item.saldoRestanteCuota)}</td>
                                                        <td className="px-6 py-4 text-center">
                                                            <div className="text-[10px] opacity-70 leading-tight">
                                                                <p className="font-bold text-slate-700">{item.metodoPago}</p>
                                                                <p>{item.cajero}</p>
                                                            </div>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Logic for Payments */}
                            {resultType === 'SOCIO_PAGOS' && totalPages > 1 && (
                                <div className="flex justify-between items-center mt-4 pt-4 border-t">
                                    <span className="text-xs text-gray-400">Página {currentPage + 1} de {totalPages}</span>
                                    <div className="flex gap-2">
                                        <button disabled={currentPage === 0} onClick={() => fetchPagosSocio(socioInfo.id, currentPage - 1)} className="px-3 py-1 border rounded text-sm hover:bg-gray-50 disabled:opacity-50 text-slate-600 font-bold flex items-center gap-1">
                                            <ChevronLeft className="w-4 h-4" /> Anterior
                                        </button>
                                        <button disabled={currentPage >= totalPages - 1} onClick={() => fetchPagosSocio(socioInfo.id, currentPage + 1)} className="px-3 py-1 border rounded text-sm hover:bg-gray-50 disabled:opacity-50 text-slate-600 font-bold flex items-center gap-1">
                                            Siguiente <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* DETALLE CREDITO MODAL */}
            {showModal && selectedCredit && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="bg-slate-900 text-white p-6 flex justify-between items-center shrink-0">
                            <div>
                                <h2 className="text-2xl font-bold flex items-center gap-3">
                                    <CreditCard className="w-6 h-6 text-green-400" />
                                    Detalle del Crédito: {selectedCredit.nroOperacion || `#${selectedCredit.id}`}
                                </h2>
                                <p className="text-slate-400 text-sm mt-1">
                                    Desembolsado: {formatDate(selectedCredit.fechaDesembolso)} | Monto: {formatCurrency(selectedCredit.monto)} | Plazo: {selectedCredit.plazoMeses} meses
                                </p>
                            </div>
                            <button
                                onClick={() => setShowModal(false)}
                                className="p-2 hover:bg-white/10 rounded-full transition"
                            >
                                <X className="w-7 h-7" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="flex-grow overflow-auto p-6 bg-slate-50">
                            {/* Modal Filters */}
                            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                                <div className="flex gap-2">
                                    <button onClick={() => { setModalFilter(''); fetchModalCuotas(selectedCredit.id, 0, modalSize, ''); }} className={`px-4 py-2 rounded-xl text-sm font-bold transition ${modalFilter === '' ? 'bg-slate-800 text-white shadow-lg' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'}`}>Todas</button>
                                    <button onClick={() => { setModalFilter('PENDIENTE'); fetchModalCuotas(selectedCredit.id, 0, modalSize, 'PENDIENTE'); }} className={`px-4 py-2 rounded-xl text-sm font-bold transition ${modalFilter === 'PENDIENTE' ? 'bg-amber-600 text-white shadow-lg' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'}`}>Pendientes</button>
                                    <button onClick={() => { setModalFilter('PAGADO'); fetchModalCuotas(selectedCredit.id, 0, modalSize, 'PAGADO'); }} className={`px-4 py-2 rounded-xl text-sm font-bold transition ${modalFilter === 'PAGADO' ? 'bg-green-600 text-white shadow-lg' : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'}`}>Pagadas</button>
                                </div>

                                <div className="flex items-center gap-3">
                                    <span className="text-sm font-bold text-slate-500 uppercase tracking-wider">Ver:</span>
                                    <select
                                        value={modalSize}
                                        onChange={(e) => {
                                            const newSize = parseInt(e.target.value);
                                            setModalSize(newSize);
                                            fetchModalCuotas(selectedCredit.id, 0, newSize, modalFilter);
                                        }}
                                        className="bg-white border border-slate-200 rounded-xl p-2 font-bold text-slate-700 outline-none focus:ring-2 focus:ring-green-500"
                                    >
                                        <option value="10">10 Cuotas</option>
                                        <option value="20">20 Cuotas</option>
                                        <option value="50">50 Cuotas</option>
                                    </select>
                                </div>
                            </div>

                            {/* Modal Table */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                {modalLoading ? (
                                    <div className="py-20 flex flex-col items-center justify-center text-slate-400">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
                                        <p className="font-medium">Cargando detalles de cuotas...</p>
                                    </div>
                                ) : modalCuotas.length === 0 ? (
                                    <div className="py-20 text-center text-slate-400">No se encontraron cuotas con los filtros seleccionados.</div>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-slate-100/50 text-slate-500 uppercase text-[10px] font-bold tracking-widest border-b border-slate-200">
                                                <th className="px-6 py-4 text-left">N° Cuota</th>
                                                <th className="px-6 py-4 text-left">Vencimiento</th>
                                                <th className="px-6 py-4 text-right">Monto Total</th>
                                                <th className="px-6 py-5 text-right">Saldo Pendiente</th>
                                                <th className="px-6 py-5 text-center">Estado</th>
                                                <th className="px-6 py-5 text-center">Pago</th>
                                            </tr>
                                        </thead>
                                            <tbody className="divide-y divide-slate-50">
                                            {modalCuotas.map((c) => {
                                                const saldo = c.saldoCuota ?? c.saldoCapitalPendiente ?? 0;
                                                const overdue = isOverdue(c.fechaVencimiento) && saldo > 0;
                                                const isRowSelected = selectedCuotaForPayment?.id === c.id;
                                                return (
                                                    <tr key={c.id} className={`transition ${overdue ? 'bg-orange-50 hover:bg-orange-100/50' : 'hover:bg-slate-50/50'}`}>
                                                        <td className="px-6 py-5 font-black text-slate-400 italic">#{c.numeroCuota}</td>
                                                        <td className={`px-6 py-5 text-center font-bold ${overdue ? 'text-orange-700' : 'text-slate-600'}`}>
                                                            {formatDate(c.fechaVencimiento)}
                                                        </td>
                                                        <td className="px-6 py-5 text-right font-black text-slate-900">
                                                            {formatCurrency(c.montoTotalCuota)}
                                                        </td>
                                                        <td className="px-6 py-5 text-right">
                                                            <span className={`font-black ${(c.saldoCuota ?? 0) > 0 ? (overdue ? 'text-red-600' : 'text-amber-600') : 'text-slate-300'}`}>
                                                                {formatCurrency(c.saldoCuota)}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-5 text-center">
                                                            <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-tight shadow-sm ${getStatusStyle(c)}`}>
                                                                {getStatusText(c)}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-5 text-center">
                                                            {(getStatusText(c).includes('CANCELADO') || getStatusText(c).includes('PARCIAL')) && (
                                                                <button
                                                                    onClick={() => handleVerDetallePago(c)}
                                                                    className="text-[10px] font-bold text-blue-600 hover:text-blue-800 underline uppercase tracking-tighter"
                                                                >
                                                                    Detalle de Pago
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {/* Modal Pagination */}
                            {!modalLoading && modalTotalPages > 1 && (
                                <div className="flex justify-between items-center mt-6">
                                    <span className="text-sm font-bold text-slate-400">Página {modalPage + 1} de {modalTotalPages}</span>
                                    <div className="flex gap-3">
                                        <button
                                            disabled={modalPage === 0}
                                            onClick={() => fetchModalCuotas(selectedCredit.id, modalPage - 1, modalSize, modalFilter)}
                                            className="px-5 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-30 transition flex items-center gap-2"
                                        >
                                            <ChevronLeft className="w-4 h-4" /> Anterior
                                        </button>
                                        <button
                                            disabled={modalPage >= modalTotalPages - 1}
                                            onClick={() => fetchModalCuotas(selectedCredit.id, modalPage + 1, modalSize, modalFilter)}
                                            className="px-5 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-30 transition flex items-center gap-2"
                                        >
                                            Siguiente <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-slate-100 flex justify-end bg-white shrink-0">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-8 py-3 bg-slate-100 text-slate-700 rounded-2xl font-bold hover:bg-slate-200 transition active:scale-95"
                            >
                                CERRAR
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* SUB-MODAL DETALLE DE PAGO */}
            {showPaymentModal && selectedCuotaForPayment && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[110] flex items-center justify-center p-4 animate-in fade-in">
                    <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-4xl overflow-hidden animate-in zoom-in-95">
                        <div className="bg-blue-600 text-white p-6 flex justify-between items-center">
                            <h3 className="text-lg font-bold">Pagos de Cuota #{selectedCuotaForPayment.numeroCuota}</h3>
                            <button onClick={() => setShowPaymentModal(false)} className="hover:bg-white/20 p-2 rounded-full transition">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-0">
                            {paymentsLoading ? (
                                <div className="py-20 flex justify-center"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>
                            ) : payments.length === 0 ? (
                                <div className="py-20 text-center text-slate-400 font-bold uppercase tracking-widest text-xs">No se registran pagos para esta cuota.</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-[11px] border-collapse">
                                        <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-black uppercase tracking-tighter">
                                            <tr>
                                                <th className="px-4 py-3 text-left">ID Pago</th>
                                                <th className="px-4 py-3 text-left">Fecha / Hora</th>
                                                <th className="px-4 py-3 text-left">Cajero / Caja</th>
                                                <th className="px-4 py-3 text-right">M. Capital</th>
                                                <th className="px-4 py-3 text-right">M. Interés</th>
                                                <th className="px-4 py-3 text-right">M. Mora</th>
                                                <th className="px-4 py-3 text-right text-green-700">Total Pago</th>
                                                <th className="px-4 py-3 text-left">Método</th>
                                                <th className="px-4 py-3 text-left">Forma</th>
                                                <th className="px-4 py-3 text-center">Transacción</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {payments.map((p, i) => (
                                                <tr key={i} className="hover:bg-blue-50/50 transition-colors">
                                                    <td className="px-4 py-3 font-bold text-slate-400 italic">#{p.id}</td>
                                                    <td className="px-4 py-3 font-medium text-slate-700">
                                                        {formatDate(p.fechaPago)} <span className="text-[9px] opacity-40 ml-1">{new Date(p.fechaPago).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-500 uppercase font-black tracking-tighter truncate max-w-[150px]" title={p.cajero}>
                                                        {p.cajero}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-bold text-slate-900">{formatCurrency(p.montoCapital || 0)}</td>
                                                    <td className="px-4 py-3 text-right font-bold text-slate-600">{formatCurrency(p.montoInteres || 0)}</td>
                                                    <td className="px-4 py-3 text-right font-bold text-red-600">{formatCurrency(p.montoMora || 0)}</td>
                                                    <td className="px-4 py-3 text-right font-black text-green-600 bg-green-50/30">{formatCurrency(p.montoPagado)}</td>
                                                    <td className="px-4 py-3 text-slate-600 font-medium">{(p.metodoPago || 'Efectivo').toUpperCase()}</td>
                                                    <td className="px-4 py-3 text-slate-600 font-medium">{(p.formaPago || 'EFECTIVO').toUpperCase()}</td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className="bg-slate-100 px-2 py-0.5 rounded text-[10px] font-mono text-slate-800 font-bold border border-slate-200">
                                                            TX-{p.transaccionId || '0000'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div className="p-6 bg-slate-50 text-right">
                            <button onClick={() => setShowPaymentModal(false)} className="px-8 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-100 transition shadow-sm">
                                CERRAR
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
